/* Dependencies */
import styled from 'styled-components';

/* Components */
import { Button, Texter } from 'lego-dist';

/* Icons */
import { ReactComponent as EditIcon } from 'images/icons/editRound.svg';
import { ReactComponent as Cancel } from 'images/icons/cancel.svg';
import { ReactComponent as Save } from 'images/icons/save.svg';
import { ReactComponent as Arrow } from 'images/icons/sortingArrowDown.svg';
import { ReactComponent as DropdownBtn } from 'images/icons/delete_dots.svg';
import magnifyingGlass from 'images/icons/magnifyingGlass.svg';

interface ClassroomDetailsImgProps {
  disable?: boolean;
}

export const Body = styled.div.attrs(() => ({
  className: 'SettingsClassroomDetailsBody',
}))`
  padding: 0 10px;
  .legoTabsItem {
    font-weight: bold !important;
  }
  ${({ theme }) => theme.breakPoints.tablet} {
    padding: 0 35px 32px;
  }
`;

export const ClassroomDetailsInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 10px 0 0 16px;

  & form {
    display: flex;
    align-items: center;
  }

  ${({ theme }) => theme.breakPoints.tablet} {
    margin: 0;
    padding: 16px 32px 0;
  }
`;
ClassroomDetailsInfoWrapper.displayName = 'SettingsClassroomDetailsInfoWrapper';

export const ClassroomDetailsImg = styled.div<ClassroomDetailsImgProps>`
  position: relative;
  pointer-events: ${({ disable }) => (disable ? 'none' : 'initial')};

  .legoPhotoUploaderContainer {
    .legoPhotoUploaderDropZone {
      .legoAvatarContainer {
        .legoAvatarSkeleton {
          padding: 0;

          .legoAvatarPhoto {
            height: 52px;
            width: 52px;
            :after {
              color: #ffffff;
              background-color: #ff4f19;
            }
          }
        }
      }
    }
  }

  ${({ theme }) => theme.breakPoints.tablet} {
    .legoPhotoUploaderContainer {
      .legoPhotoUploaderDropZone {
        .legoAvatarContainer {
          .legoAvatarSkeleton {
            .legoAvatarPhoto {
              height: 80px;
              width: 80px;
            }
          }
        }
      }
    }
  }
`;
ClassroomDetailsImg.displayName = 'SettingsClassroomDetailsImg';

export const ClassroomDetailsName = styled.div.attrs(() => ({
  className: 'SettingsClassroomDetailsName',
}))`
  color: ${({ theme }) => theme.colors.darkGray};
  font-size: 14px;
  font-weight: bold;
  margin: auto 10px auto 16px;
  max-width: 288px;
  word-break: break-all;

  ${({ theme }) => theme.breakPoints.tablet} {
    font-size: 18px;
  }
`;
ClassroomDetailsName.displayName = 'SettingsClassroomDetailsName';

export const Overlay = styled.div`
  background-color: ${({ theme }) => theme.colors.blackAlpha};

  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  height: 100vh;
  width: 100vw;
  z-index: 10000;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: none;
  }
`;
Overlay.displayName = 'SettingsClassroomDetailsOverlay';

export const ClassroomDetailsEditWrapperMobile = styled.div.attrs(() => ({
  className: 'SettingsClassroomDetailsEditWrapperMobile',
}))`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;

  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;

  background-color: ${({ theme }) => theme.colors.white};
  padding: 40px 16px 16px 16px;
  width: 100%;
  z-index: 11000;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: none;
  }
`;
ClassroomDetailsEditWrapperMobile.displayName =
  'SettingsClassroomDetailsEditWrapperMobile';

export const ClassroomDetailsEditWrapperDesktop = styled.div`
  display: none;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: flex;
    align-items: center;
    position: relative;
  }
`;
ClassroomDetailsEditWrapperDesktop.displayName =
  'SettingsClassroomDetailsEditWrapperDesktop';

export const ClassroomDetailsNameCharCount = styled.div`
  color: ${({ theme }) => theme.colors.shadeMedium};
  font-size: 14px;
  position: absolute;
  top: 18px;
  right: 16px;

  ${({ theme }) => theme.breakPoints.tablet} {
    top: 0px;
    right: 74px;
  }
`;
ClassroomDetailsNameCharCount.displayName =
  'SettingsClassroomDetailsNameCharCount';

export const ClassroomDetailsEditName = styled(Texter).attrs(() => ({
  className: 'SettingsClassroomDetailsEditName',
}))`
  > input {
    height: 40px;
    width: 100%;

    border: 1px solid ${({ theme }) => theme.colors.shadeMedium};
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.shadeMediumDark} !important;
    font-size: 16px !important;
    font-weight: normal;
    line-height: 16px;
    margin-bottom: 25px;
    padding: 0 0 0 12px;

    &:focus {
      border: 1px solid ${({ theme }) => theme.colors.physicalLight30};
    }
  }

  ${({ theme }) => theme.breakPoints.tablet} {
    > input {
      margin: auto 10px auto 16px;
      width: 288px;
    }
  }
`;

export const EditBtn = styled(EditIcon).attrs(() => ({
  className: 'SettingsClassroomDetailsNameEditButton',
}))`
  cursor: pointer;
  height: 24px;
  width: 24px;

  & .pen {
    fill: ${({ theme }) => theme.colors.shadeMediumDark};
  }

  &:hover .pen {
    fill: #85daed;
  }
`;

export const EditBtnImg = styled(EditIcon).attrs(() => ({
  className: 'SettingsClassroomDetailsNameEditImageButton',
}))`
  cursor: pointer;
  height: 20px;
  width: 20px;

  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 10;

  & .pen {
    fill: ${({ theme }) => theme.colors.shadeMediumDark};
  }

  &:hover .pen {
    fill: #85daed;
  }

  ${({ theme }) => theme.breakPoints.tablet} {
    height: 24px;
    width: 24px;
  }
`;

export const EditActions = styled.div.attrs(() => ({
  className: 'SettingsClassroomDetailsEditActions',
}))`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  ${({ theme }) => theme.breakPoints.tablet} {
    justify-content: flex-start;
  }
`;

export const EditCancelMobile = styled(Button).attrs(() => ({
  className: 'SettingsClassroomDetailsEditCancelMobile',
}))`
  border: 1px solid ${({ theme }) => theme.colors.shadeMediumDark};
  color: ${({ theme }) => theme.colors.shadeMediumDark};

  height: 32px;
  width: 100%;
`;

export const EditCancel = styled(Cancel).attrs(() => ({
  className: 'SettingsClassroomDetailsEditCancel',
}))`
  cursor: pointer;
  height: 20px;
  width: 20px;

  & .icon {
    fill: ${({ theme }) => theme.colors.shadeMediumDark};
  }
`;

export const EditSaveMobile = styled(Button).attrs(() => ({
  className: 'SettingsClassroomDetailsEditSaveMobile',
}))`
  height: 32px;
  width: 100%;
`;

export const EditSaveDesktop = styled(Button).attrs(() => ({
  className: 'SettingsClassroomDetailsEditSaveDesktop',
}))`
  padding: 0;
`;

export const EditSave = styled(Save).attrs(() => ({
  className: 'SettingsClassroomDetailsEditSave',
}))`
  cursor: pointer;
  height: 24px;
  width: 24px;
`;

export const ClassroomDetailsTab = styled.div.attrs(() => ({
  className: 'SettingsClassroomDetailsTabView',
}))`
  padding: 0 10px;
  width: 100%;

  ${({ theme }) => theme.breakPoints.tablet} {
    padding-left: 0;
    padding-right: 0;
  }
`;

export const CenterHeader = styled.div.attrs(() => ({
  className: 'SettingsClassroomDetailsHeader',
}))`
  display: flex;
  align-items: center;
  justify-content: space-between;

  height: 60px;
  width: 100%;

  div:nth-child(3) {
    display: none;
  }
`;

export const CenterCount = styled.div.attrs(() => ({
  className: 'SettingsClassroomDetailsCount',
}))`
  display: none;

  color: ${({ theme }) => theme.colors.shadeDark};
  font-size: 16px;
  font-weight: bold;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: block;
  }
`;

export const CenterActions = styled.div.attrs(() => ({
  className: 'SettingsClassroomDetailsActions',
}))`
  display: flex;
  align-items: center;
  width: 100%;

  ${({ theme }) => theme.breakPoints.tablet} {
    width: fit-content;
  }
`;

export const SearchBar = styled(Texter).attrs(() => ({
  className: 'SettingsClassroomDetailsSearchBar',
}))`
  margin-right: 16px;

  & .texter-input {
    border: 1px solid #acc2d3;
    border-radius: 20px;
    color: #8295a3;
    font-size: 14px;
    font-style: normal;
    font-weight: normal;
    padding-left: 16px;
    padding-right: 40px;
    height: 40px;
    width: 100%;
  }

  & .texter-input::placeholder {
    color: #8295a3;
    font-style: normal;
    font-weight: normal;
    vertical-align: center;
  }

  ${({ theme }) => theme.breakPoints.tablet} {
    & .texter-input {
      width: 180px;
    }
  }

  display: ${({ showSearcher = true }) => (showSearcher ? 'flex' : 'none')};
`;

export const SearchIcon = styled.img.attrs(() => ({
  className: 'SettingsClassroomDetailsSearchIcon',
  src: magnifyingGlass,
}))`
  top: 6px;
  width: 20px;
  right: 16px;
  height: 30px;
  position: absolute;
`;

export const PrimaryButtonMobile = styled(Button).attrs(() => ({
  className: 'SettingsClassroomDetailsPrimaryButtonMobile',
}))`
  font-size: 35px;
  height: 40px;
  width: 40px;

  display: flex;
  align-items: center;
  justify-content: center;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: none;
  }
`;

export const PrimaryButton = styled(Button).attrs(() => ({
  className: 'SettingsClassroomDetailsPrimaryButton',
}))`
  display: none;
  min-width: fit-content;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: block;
    min-width: 170px;
  }
`;

export const ClassroomDetailsList = styled.div`
  width: 100%;
`;
ClassroomDetailsList.displayName = 'SettingsClassroomDetailsList';

export const ClassroomDetailsListHeader = styled.div`
  display: grid;
  font-size: 14px;
  align-items: center;
  margin-bottom: 10px;
  grid-template-rows: 40px;
  grid-template-columns: 3fr 1fr 1.5fr;
  color: ${({ theme }) => theme.colors.shadeDark};
  border-bottom: 1px solid ${({ theme }) => theme.colors.shadeMediumLight};
`;
ClassroomDetailsListHeader.displayName = 'SettingsClassroomDetailsListHeader';

export const ClassroomDetailsListHeaderName = styled.div`
  display: flex;
  align-items: center;
  &:not(:first-child) {
    justify-content: center;
  }
  &:last-child {
    padding-right: 32%;
  }
`;
ClassroomDetailsListHeaderName.displayName =
  'SettingsClassroomDetailsListHeaderName';

export const SortArrow = styled(Arrow)`
  height: 10px;
  width: 14px;

  margin-left: 20px;

  &:hover {
    cursor: pointer;
  }
`;
SortArrow.displayName = 'SettingsClassroomDetailsListUpArrow';

export const ListHeaderClassrooms = styled.div`
  display: none;
  grid-column: 11 / 15;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: block;
  }
`;
ListHeaderClassrooms.displayName =
  'SettingsClassroomDetailsListHeaderClassrooms';

export const ClassroomDetailsListItem = styled.div`
  display: grid;
  align-items: center;
  grid-template-rows: 60px 30px;
  grid-template-columns: 10fr 3fr 4fr 1fr;

  border-radius: 4px;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.shadeDark};
  border: 1px solid ${({ theme }) => theme.colors.shadeMediumLight};

  ${({ theme }) => theme.breakPoints.tablet} {
    row-gap: 10px;
    align-items: center;
    grid-template-rows: 64px;
  }

  &:hover {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.darkGray};
    border: 1px solid ${({ theme }) => theme.colors.physicalLight30};
  }
`;
ClassroomDetailsListItem.displayName = 'SettingsClassroomDetailsListItem';

export const ClassroomDetailsListFirstColumn = styled.div`
  display: flex;
  padding: 0 12px;
  font-size: 14px;
  font-weight: bold;
  align-items: center;

  & div {
    display: flex;
    margin-left: 5px;
    align-items: center;
    p {
      margin-left: 16px;
      max-width: 170px;
      word-wrap: break-word;
    }
  }

  ${({ theme }) => theme.breakPoints.tablet} {
    height: 100%;
    padding: 0 10px;
    font-size: 16px;
    & p {
      max-width: 320px;
    }
  }
`;
ClassroomDetailsListFirstColumn.displayName =
  'SettingsClassroomDetailsListFirstColumn';

export const ClassroomDetailsListFirstColumnImageName = styled.div`
  width: 100%;
  height: 100%;
`;
ClassroomDetailsListFirstColumnImageName.displayName =
  'SettingsClassroomDetailsListFirstColumnImageName';

export const ClassroomDetailsListSecondColumn = styled.div`
  display: flex;
  padding: 0 12px;
  font-size: 14px;
  align-items: center;
  justify-content: center;

  & > span {
    display: block;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 90%;
    max-width: 125px;
  }
`;
ClassroomDetailsListSecondColumn.displayName =
  'SettingsClassroomDetailsListSecondColumn';

export const ClassroomDetailsListItemImg = styled.img.attrs(() => ({
  alt: 'avatar',
}))`
  width: 40px;
  height: 40px;
  overflow: hidden;
  object-fit: cover;
  border-radius: 50%;
`;
ClassroomDetailsListItemImg.displayName = 'SettingsClassroomDetailsListItemImg';

export const ClassroomDetailsListThirdColumn = styled.div`
  display: flex;
  justify-content: center;

  & > span {
    display: block;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 90%;
  }
`;
ClassroomDetailsListThirdColumn.displayName = 'ClassroomDetailsListThirdColumn';

export const ClassroomDetailsListItemDropdown = styled.div.attrs(() => ({
  className: 'SettingsClassroomDetailsListItemDropdown',
}))`
  display: flex;
  justify-content: center;
  min-width: 50px;
`;
ClassroomDetailsListItemDropdown.displayName =
  'SettingsClassroomDetailsListItemDropdown';

export const ItemDropdown = styled.div.attrs({ className: 'ItemDropdown' })`
  display: flex;
  cursor: pointer;
  text-align: center;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.white};

  & span {
    color: ${({ theme }) => theme.colors.shadeDark};
    font-size: 14px;
    padding: 7px 15px;
    text-align: left;
    width: 100%;

    &.delete {
      color: ${({ theme }) => theme.colors.health} !important;
    }
  }

  & span:hover {
    cursor: pointer;
    background-color: #0086d810;
    color: ${({ theme }) => theme.colors.darkGray};
  }

  @media only screen and (max-width: 600px) {
    position: fixed;
    bottom: 0;
    right: 0;
    left: 0;
    padding: 16px;
    width: 100%;
    z-index: 11000;
  }

  ${({ theme }) => theme.breakPoints.tablet} {
    border: 1px solid ${({ theme }) => theme.colors.shadeMediumLight};
    border-radius: 4px;
    box-shadow: 0 2px 4px 0 #00000015;
    min-width: 157px;
    padding: 9px 0;
  }
`;

export const Dots = styled(DropdownBtn).attrs(() => ({
  className: 'ItemDropdownButton',
}))`
  height: 50px;
`;

export const ConfigHeader = styled.div.attrs(() => ({
  className: 'SettingsClassroomDetailsConfigHeader',
}))`
  height: 60px;
  display: flex;
  font-size: 24px;
  align-items: center;
  color: ${({ theme }) => theme.colors.darkGray};
`;

export const ClassroomDetailsConfigFormWrapper = styled.div`
  position: relative;

  .legoForm {
    padding: 20px 0;
    justify-content: flex-start;
    color: ${({ theme }) => theme.colors.darkGray};

    .legoFormFieldset {
      max-width: 212px;
      .legoSelecterInput {
        color: ${({ theme }) => theme.colors.darkGray};
      }
      .legoSelecterOptionsContainer {
        overflow: auto;
        max-height: 200px;
      }
    }

    #legoFormFieldset-min_age {
      margin-right: 20px;
    }

    #classroomAgeTitle p {
      font-size: 14px;
    }

    .legoFormSubmitButton {
      :disabled {
        background-color: ${({ theme }) => theme.colors.shadeMediumDark};
      }
    }
  }
`;
ClassroomDetailsConfigFormWrapper.displayName =
  'SettingsClassroomDetailsConfigFormWrapper';

export const ConfigItem = styled.div.attrs(() => ({
  className: 'SettingsClassroomDetailsConfigItem',
}))``;

export const Label = styled.div.attrs(() => ({
  className: 'SettingsClassroomDetailsConfigItemLabel',
}))`
  color: ${({ theme }) => theme.colors.darkGray};
  font-size: 14px;
  height: 32px;
  margin-bottom: 5px;

  display: flex;
  align-items: center;
`;

export const SaveBtn = styled.div.attrs(() => ({
  className: 'SettingsClassroomDetailsConfigSaveButtonWrapper',
}))`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const ErrorMessage = styled.div.attrs(() => ({
  className: 'SettingsClassroomDetailsInputError',
}))`
  position: absolute;
  bottom: 70px;
  left: 16px;

  color: #e02020;
  font-size: 14px;

  ${({ theme }) => theme.breakPoints.tablet} {
    bottom: -20px;
    left: 16px;
  }
`;
